import './styles.scss';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Popconfirm from 'antd/lib/popconfirm';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { CheckCircleFilled, CloseCircleFilled, CloseOutlined } from '@ant-design/icons';

import { getCore } from 'cvat-core-wrapper';
import { plural, t } from 'utils/i18n';
import LibraryThumb from '../references/library-thumb';
import { previewUrl } from '../references/reference-model';
import type { Scene, SceneMode } from './scene-api';
import { useSceneContext } from './scene-context';
import ScenePicker, { NoModelThumb } from './scene-picker';
import {
    explains, fallbackLines, folderLines, isFloor, OverrideLine, ROOMS_STEP, rowName, rowTag, SceneRow, sceneRows,
    unusedNote,
} from './scene-model';

// the rest stays behind "N more": a hundred walls must not push the other rows away
const OVERRIDES_SHOWN = 5;

interface OverridesProps {
    row: SceneRow;
    backendAPI: string;
}

// The shapes holding a value other than the scene one: shown on the drawing, put back one by one or all at once
function Overrides({ row, backendAPI }: OverridesProps): JSX.Element | null {
    const context = useSceneContext();
    const [all, setAll] = useState(false);
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    if (!context || row.value === null) return null;
    const {
        jobId, apply, showShape, highlightShape,
    } = context;
    const { value } = row;
    const shown = all ? row.overrides : row.overrides.slice(0, OVERRIDES_SHOWN);
    const model = row.kind === 'model';
    const replace = async (shapes?: number[]): Promise<void> => {
        setBusy(true);
        setFailure(null);
        try {
            await apply(row.id, value, !shapes, shapes);
        } catch (error) {
            setFailure(error instanceof Error ? error.message : String(error));
        } finally {
            setBusy(false);
        }
    };
    const meta = (line: OverrideLine): string => {
        const place = t('Drawing {n} · #{id}', { n: line.frame + 1, id: line.id });
        return line.job === jobId ? place : `${place} · ${t('job {id}', { id: line.job })}`;
    };
    const question = model ? 'Replace the model of {count} with «{name}»?' : 'Replace the material of {count} with «{name}»?';
    const consequence = model ?
        'Previously assigned models will be replaced with the scene model.' :
        'Previously assigned materials will be replaced with the scene material.';
    return (
        <div className='cvat-scene-overrides'>
            {shown.map((line) => (
                <div
                    key={line.id}
                    className='cvat-scene-override'
                    onMouseEnter={() => highlightShape(line.id)}
                    onMouseLeave={() => highlightShape(null)}
                    onFocus={() => highlightShape(line.id)}
                    onBlur={() => highlightShape(null)}
                >
                    {model && !line.value ? <NoModelThumb className='cvat-scene-override-thumb' /> : (
                        <LibraryThumb
                            className='cvat-scene-override-thumb'
                            src={previewUrl(line.preview ?? undefined, backendAPI)}
                            fullSrc={previewUrl(line.preview ?? undefined, backendAPI, true)}
                        />
                    )}
                    <button
                        type='button'
                        className='cvat-scene-override-show'
                        title={t('Show on the drawing')}
                        onClick={() => showShape(line)}
                    >
                        <span className='cvat-scene-override-name'>{line.name}</span>
                        <span className='cvat-scene-override-meta'>{meta(line)}</span>
                    </button>
                    <Button
                        type='link'
                        size='small'
                        disabled={busy}
                        title={t('Replace with «{name}»', { name: row.sceneName })}
                        onClick={() => replace([line.id])}
                    >
                        {t('Replace')}
                    </Button>
                </div>
            ))}
            {row.overrides.length > shown.length && (
                <Button type='link' size='small' onClick={() => setAll(true)}>
                    {t('{n} more', { n: row.overrides.length - shown.length })}
                </Button>
            )}
            <Popconfirm
                title={(
                    <div className='cvat-scene-overrides-confirm'>
                        <Text strong>{t(question, { count: plural(row.overrides.length, 'object:of'), name: row.sceneName })}</Text>
                        <Text type='secondary'>{t(consequence)}</Text>
                    </div>
                )}
                okText={t('Replace')}
                cancelText={t('Cancel')}
                onConfirm={() => replace()}
            >
                <Button type='link' size='small' disabled={busy}>
                    {t('Replace all with «{name}»', { name: row.sceneName })}
                </Button>
            </Popconfirm>
            {failure && <Text type='danger'>{failure}</Text>}
        </div>
    );
}

interface RowProps {
    row: SceneRow;
    mode: SceneMode;
    backendAPI: string;
    // absent when there is nothing to set: no shapes, or not used in this mode
    onOpen?(): void;
}

function RowView({
    row, mode, backendAPI, onOpen,
}: RowProps): JSX.Element {
    const tag = rowTag(row);
    const name = rowName(row, mode);
    const explained = explains(row);
    const another = row.kind === 'model' ? '+{n} with another model' : '+{n} with another material';
    const [expanded, setExpanded] = useState(false);
    // nothing to put back where the row sets nothing
    const listed = !!onOpen && row.overrides.length > 0;
    return (
        <div className={`cvat-scene-row cvat-scene-row-${row.status}`}>
            {row.status === 'none' ? <NoModelThumb className='cvat-scene-row-thumb' /> : (
                <LibraryThumb
                    className='cvat-scene-row-thumb'
                    src={previewUrl(row.preview ?? undefined, backendAPI)}
                    fullSrc={previewUrl(row.preview ?? undefined, backendAPI, true)}
                />
            )}
            <div className='cvat-scene-row-text'>
                <div className='cvat-scene-row-title'>
                    <Text strong>{t(row.title)}</Text>
                    {tag && <Tag color={tag.color}>{tag.text}</Tag>}
                </div>
                {explained ? (
                    <Text type='secondary' className='cvat-scene-row-name'>{name}</Text>
                ) : (
                    <Text className='cvat-scene-row-name' ellipsis={{ tooltip: name }}>{name}</Text>
                )}
                {row.count > 0 && (
                    <Text type='secondary' className='cvat-scene-row-count'>
                        {plural(row.count, 'object')}
                        {row.exceptions > 0 && !listed && ` · ${t(another, { n: row.exceptions })}`}
                        {listed && (
                            <>
                                {' · '}
                                <Button
                                    type='link'
                                    size='small'
                                    className='cvat-scene-row-toggle'
                                    aria-expanded={expanded}
                                    onClick={() => setExpanded(!expanded)}
                                >
                                    {`${t(another, { n: row.exceptions })} ${expanded ? '▴' : '▾'}`}
                                </Button>
                            </>
                        )}
                        {unusedNote(row, mode) && ` · ${unusedNote(row, mode)}`}
                    </Text>
                )}
                {/* an explicit button, as in the references panel: the preview click opens the full view */}
                {onOpen && (
                    <Button type='link' size='small' className='cvat-scene-pick' onClick={onOpen}>
                        {t('Pick')}
                    </Button>
                )}
            </div>
            {listed && expanded && <Overrides row={row} backendAPI={backendAPI} />}
        </div>
    );
}

interface FolderProps {
    scene: Scene;
    projectId: number | null;
    backendAPI: string;
}

function FolderBlock({ scene, projectId, backendAPI }: FolderProps): JSX.Element {
    const lines = folderLines(scene);
    const fallbacks = fallbackLines(scene);
    const shownPath = scene.folder_windows ?? scene.folder;
    return (
        <section className='cvat-scene-section'>
            <Text strong>{t('Project folder')}</Text>
            {shownPath ? (
                <Text className='cvat-scene-folder-path' copyable={{ text: shownPath }} ellipsis={{ tooltip: shownPath }}>
                    {shownPath}
                </Text>
            ) : (
                <div className='cvat-scene-folder-none'>
                    <Text type='secondary'>{t('No project folder: the scene is built from the fallbacks')}</Text>
                    {projectId !== null && (
                        <Link to={`/projects/${projectId}/settings`}>{t('Choose a folder')}</Link>
                    )}
                </div>
            )}
            {scene.folder && !scene.indexed && (
                <Alert
                    type='info'
                    showIcon
                    message={t('The folder is not indexed yet: project materials and fallback names appear after indexing')}
                />
            )}
            {lines?.map((line) => (
                <div key={line.path} className={`cvat-scene-folder-line ${line.present ? '' : 'cvat-scene-folder-missing'}`}>
                    {line.present ?
                        <CheckCircleFilled aria-label={t('present')} /> :
                        <CloseCircleFilled aria-label={t('missing')} />}
                    <span>
                        <span className='cvat-scene-folder-name'>{line.path}</span>
                        {` — ${t(line.note)}`}
                        {line.blocksExport && (
                            <Text type='danger' className='cvat-scene-folder-block'>
                                {t('VR export will not start until it exists')}
                            </Text>
                        )}
                    </span>
                </div>
            ))}
            <Collapse ghost className='cvat-scene-fallbacks'>
                <Collapse.Panel key='fallbacks' header={t('What assembly takes when the project lacks something')}>
                    {fallbacks.length === 0 && (
                        <Text type='secondary'>
                            {scene.folder ?
                                t('The names appear once the project folder is indexed') :
                                t('Without a project folder the names are not known')}
                        </Text>
                    )}
                    {fallbacks.map((line) => (
                        <div key={line.title} className='cvat-scene-fallback-line'>
                            <LibraryThumb
                                className='cvat-scene-fallback-thumb'
                                src={previewUrl(line.preview ?? undefined, backendAPI)}
                                fullSrc={previewUrl(line.preview ?? undefined, backendAPI, true)}
                            />
                            <span>{`${t(line.title)}: ${line.name}`}</span>
                            {line.missing && <Tag color='red'>{t('Not in its file')}</Tag>}
                        </div>
                    ))}
                    <div className='cvat-scene-fallback-line'>
                        <span>{`${t('Countertops')}: ${t('the material of the furniture model')}`}</span>
                    </div>
                </Collapse.Panel>
            </Collapse>
        </section>
    );
}

// The scene settings of the whole task; takes the sidebar's place while open
export default function ScenePanel(): JSX.Element | null {
    const context = useSceneContext();
    const [picking, setPicking] = useState<string | null>(null);
    if (!context) return null;
    const {
        scene, projectId, loading, error, unsaved, toggle, setMode, apply,
    } = context;
    const { backendAPI } = getCore().config;
    const rows = scene ? sceneRows(scene) : [];
    const materials = rows.filter((row) => row.kind === 'material' && !isFloor(row.id));
    const floors = rows.filter((row) => isFloor(row.id));
    const models = rows.filter((row) => row.kind === 'model');
    const picked = scene?.items.find((item) => item.id === picking) ?? null;
    // models come from one library in both modes: curtains and tulle are picked in FP too, for the VR export
    const opener = (row: SceneRow): (() => void) | undefined => (
        row.status === 'empty' || (row.status === 'unused' && row.kind !== 'model') ?
            undefined :
            () => setPicking(row.id)
    );
    const rowView = (row: SceneRow, mode: SceneMode): JSX.Element => (
        <RowView key={row.id} row={row} mode={mode} backendAPI={backendAPI} onOpen={opener(row)} />
    );
    return (
        <div className='cvat-scene-panel'>
            <div className='cvat-scene-panel-header'>
                <div>
                    <Text strong className='cvat-scene-panel-title'>{t('Scene')}</Text>
                    <Text type='secondary'>{t('Materials and models for the whole task')}</Text>
                </div>
                <Button type='text' size='small' aria-label={t('Close')} icon={<CloseOutlined />} onClick={toggle} />
            </div>
            <Radio.Group
                className='cvat-scene-mode'
                optionType='button'
                buttonStyle='solid'
                size='small'
                value={scene?.mode}
                disabled={!scene || loading}
                onChange={(event) => setMode(event.target.value)}
            >
                <Radio.Button value='FP'>FP</Radio.Button>
                <Radio.Button value='VR'>VR</Radio.Button>
            </Radio.Group>
            {unsaved && (
                <Alert type='info' showIcon message={t('Unsaved changes are not counted: save the job to see them')} />
            )}
            {error && <Alert type='error' showIcon message={error} />}
            {!scene ? <Spin className='cvat-scene-loading' /> : (
                <>
                    <FolderBlock scene={scene} projectId={projectId} backendAPI={backendAPI} />
                    <section className='cvat-scene-section'>
                        <Text strong>{t('Materials')}</Text>
                        {materials.map((row) => rowView(row, scene.mode))}
                        <Text type='secondary' className='cvat-scene-subtitle'>{t('Floors')}</Text>
                        {floors.map((row) => rowView(row, scene.mode))}
                        {floors.length === 0 && (
                            <div className='cvat-scene-row cvat-scene-row-empty'>
                                <Text type='secondary' className='cvat-scene-row-name'>
                                    {t('No rooms on the drawings: run “{step}” to get the floors', { step: t(ROOMS_STEP) })}
                                </Text>
                            </div>
                        )}
                    </section>
                    <section className='cvat-scene-section'>
                        <Text strong>{t('Models')}</Text>
                        <Text type='secondary' className='cvat-scene-subtitle'>
                            {t('From the shared library: the scene links them')}
                        </Text>
                        {models.map((row) => rowView(row, scene.mode))}
                    </section>
                </>
            )}
            {scene && picked && (
                <ScenePicker
                    item={picked}
                    mode={scene.mode}
                    backendAPI={backendAPI}
                    onApply={(value, includeExceptions) => apply(picked.id, value, includeExceptions)}
                    onClose={() => setPicking(null)}
                />
            )}
        </div>
    );
}
